import logo from "../assets/moa-logo-full-inline.svg";
import { Img } from "./image";


import { Box, CircularProgress } from "@mui/material";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { login, resendTwoFactorCode, switchToStandardView, verifyTwoFactor } from '../api/auth';
import { AccountContext } from '../context/account';
import { MapDataContext } from "../context/map-data";
import { FullAccount } from "../core/global";
import { introSignUpMessage } from "../data/content";
import { loginShape, twoFactorShape } from '../data/form-shapes';
import { isBrowser } from '../helpers/common';
import { validatePassword } from '../helpers/core';
import useLoggedIn from '../hooks/useLoggedIn';
import styles from '../styles/components/login-modal.module.scss';
import { ErrorMessage, WarningMessage } from './error-message';
import { CreateForm } from './form-components';
import Loader from './loader';
import { ModalTitle } from './modal-title';
import { PoweredByLink } from "./mui-login-page-wrapper";
import { StyledMarkdown } from "./typeface";

interface LoginModalProps {
  open: boolean;
  handleClose: () => void;
}

export const checkUserPassword = (password: string, password_two: string, setPageError?: (error: string[]) => void) => {
  let isValidPassword = false
  if (password && password_two) {
    const {isValid} = validatePassword(password)
    const isMatching = !!(password === password_two)
    if ( !isMatching || (isMatching && !isValid)) {
      if (setPageError) {
        const failedPasswordMessage = isValid ? [] : ['Your password must be at least **6 characters long** and **contain both letters and numbers**.']
        const matchingMessage = isMatching ? [] : ['Both passwords must be matching.']
        setPageError([...matchingMessage, ...failedPasswordMessage])
      }
    } else {
      if (setPageError) {
       setPageError([])
      }
      isValidPassword = true
    }
  }
  
  return isValidPassword
}

export const LoginModal = ({ open, handleClose }: LoginModalProps) => {
  const [isEdited, setIsEdited] = useState(false)
  const [modalError, setModalError] = useState<string[]>([]);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [showSuperAdminSelector, setShowSuperAdminSelector] = useState(false)
  const [userAccountTemp, setUserAccountTemp] = useState<FullAccount | null>(null)
  const [show2fa, setShow2fa] = useState(false);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const onVerifiedRef = useRef<(account: any) => void>(() => {});
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendNotice, setResendNotice] = useState<string>('');

  const { setAccount } = useContext(AccountContext);
  const { refetchDataSources } = useContext(MapDataContext);

  const isLoggedIn = useLoggedIn()

  const validateInputs = useCallback(() => {
    let isValid = true;
    const errors: string[] = []
    if (!email || (email && !/\S+@\S+\.\S+/.test(email))) {
      errors.push('Please enter a valid email address.');
    }
    if (!password || (password && password.length < 6)) {
      errors.push('Password must be at least 6 characters long.');
    }    
    if (errors.length > 0) {
      isValid = false;
    }
    setModalError(errors);
    return isValid;
  }, [email, password]);

  const onClose = useCallback(() => {
    setModalError([])
    setIsEdited(false)
    handleClose()
  }, [])

  const onSubmit = useCallback(() => {
    setModalError([])
    const isValid = validateInputs()
    if (isValid) {
      setLoading(true)
      const execute = async () => {
        const result = await login({
          email,
          password,
          rememberMe,
        })
        const {error, message, response} = result
        if (error !== 200) {
          setModalError( e => [...e, `${message}`])
        } else {
          if (response.twoFactorRequired) {
            startTwoFactor(response, handleAccountResponse)
          } else {
            handleAccountResponse(response.account)
          }
        }
        setLoading(false)
      }
      execute()
    }
  }, [email, password, rememberMe]);
  

  // Normal login success (your existing level-100 branch, extracted).
  const handleAccountResponse = useCallback((account: any) => {
    const { level } = account;
    isBrowser() && localStorage.setItem("token", account.token);
    if (level === 100) {
      setShowSuperAdminSelector(true);
      setUserAccountTemp({ loggedIn: true, checked: true, ...account });
      setShow2fa(false)
    } else {
      refetchDataSources();
      setAccount({ loggedIn: true, checked: true, ...account });
      navigate("/login");
    }
  }, [refetchDataSources, setAccount]);

  // Super-admin "login without selecting a view" success (extracted).
  const handleDowngradeAccount = useCallback((account: any) => {
    setAccount({ loggedIn: true, checked: true, ...account, level: 90 });
    isBrowser() && localStorage.setItem("token", account.token);
    refetchDataSources();
    navigate("/login");
  }, [refetchDataSources, setAccount]);

  const startTwoFactor = useCallback((response: any, onVerified: (account: any) => void) => {
    setChallengeToken(response.challengeToken);
    onVerifiedRef.current = onVerified;
    setShow2fa(true);
    setResendCooldown(30); // first code just went out — start the timer immediately
  }, []);

  const onVerifyCode = useCallback(async (vals: any) => {
    return verifyTwoFactor({ code: `${vals.code}`.trim(), token: `${challengeToken}` });
  }, [challengeToken]);

  const onVerifySuccess = useCallback((response: any) => {
    if (isBrowser() && response.device_token) {
      localStorage.setItem("device_token", response.device_token); // remember this browser for 30 days
    }
    onVerifiedRef.current?.(response.account);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const onResendCode = useCallback(async () => {
    setResendNotice('');
    setModalError([]);
    const { error, message, response } = await resendTwoFactorCode({ token: `${challengeToken}` });
    // const { error, message, response } = await resendTwoFactorCode();
    if (error !== 200) {
      setModalError((e) => [...e, `${message}`]);
    } else {
      if (response?.challengeToken) setChallengeToken(response.challengeToken); // swap to the fresh token
      setResendNotice('A new code has been sent to your email.');
      setResendCooldown(30);
    }
  }, [challengeToken]);
  
  const twoFactorDefaults = useRef({ code: '' }).current;

  let navigate = useNavigate();
  
  if (isLoggedIn) {
    return <></>
  }

  return (
    <>
      <Dialog
        id='login-modal'
        open={open}
        onClose={onClose}
        // fullWidth
        maxWidth="md"
      >
        <ModalTitle onClose={onClose}></ModalTitle>
        <DialogContent
          className={styles.dialogContent}
        >
          <div className={styles.leftPanel}>
            <Img className={styles.moaLogo} src={logo} alt='MetOceanAtlas logo' />
            <div>
              <StyledMarkdown>
                {introSignUpMessage}
              </StyledMarkdown>
            </div>
            <PoweredByLink />
          </div>
          <div className={styles.rightPanel}>
            <ErrorMessage message={modalError.join(' ')} />
            {(!loading && !showSuperAdminSelector) && <p><strong>Sign in</strong> to access your dashboard, tools, and project updates. Or <Link to="/sign-up">Create a free account</Link></p>}
            {loading ? 
              <Loader center />
              :
              show2fa ?
                <div className={styles.column}>
                  <p>We've emailed a 6-digit code to <strong>{email}</strong>. Enter it below to finish signing in.</p>
                  <CreateForm
                    key="twofa-form"
                    size="medium"
                    shape={twoFactorShape}
                    oneButtonSubmit
                    submitButtonLabel="Verify and sign in"
                    defaultValues={twoFactorDefaults}
                    onSubmit={onVerifyCode}
                    onSuccess={onVerifySuccess}
                  />
                  {resendNotice && <WarningMessage message={resendNotice} />}
                  {resendCooldown > 0 ? (
                    <div className={styles.resendCountdown}>
                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <CircularProgress
                          variant="determinate"
                          value={(resendCooldown / 30) * 100}
                          size={36}
                        />
                        <Box
                          sx={{
                            top: 0, left: 0, bottom: 0, right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <span style={{ fontSize: '0.7rem' }}>{resendCooldown}</span>
                        </Box>
                      </Box>
                      <p>You can resend the code in {resendCooldown} second{resendCooldown === 1 ? '' : 's'}.</p>
                    </div>
                  ) : (
                    <Button
                      size="small"
                      color="primary"
                      onClick={onResendCode}
                    >
                      Resend code
                    </Button>
                  )}
                  <Button
                    size="small"
                    onClick={() => {
                      setShow2fa(false);
                      setChallengeToken(null);
                      setModalError([]);
                      setResendNotice('');
                      setResendCooldown(0);
                    }}
                  >
                    Use a different account
                  </Button>
                </div>
              :
                <>
                  {(showSuperAdminSelector && userAccountTemp) ?
                    <div className={styles.column}>
                      <p>Hi <strong>{userAccountTemp?.first_name}</strong>, you have super admin access. Please select how you want to view the platform:</p>
                      <Button 
                        variant="contained" 
                        onClick={() => {
                          if (!userAccountTemp) return
                          setLoading(true)
                          const execute = async () => {
                            // Already authenticated as super admin (2FA is done). Downgrading is a
                            // token reissue from the session we already have — not a new login,
                            // so there's no second code.
                            isBrowser() && localStorage.setItem("token", `${userAccountTemp.token}`)
                            const { error, message, response } = await switchToStandardView({})
                            if (error !== 200) {
                              setModalError(e => [...e, `${message}`])
                            } else {
                              handleDowngradeAccount(response.account)
                            }
                            setLoading(false)
                          }
                          execute()
                        }}
                      >
                        Login with default dashboard view
                      </Button>

                      <Button 
                        variant="contained" 
                        color="warning"
                        onClick={() =>{
                          setAccount({
                            ...userAccountTemp,
                          })
                          isBrowser() && localStorage.setItem("token", `${userAccountTemp.token}`);
                          refetchDataSources()
                          navigate("/login");
                        }}
                      >
                        Login as super admin
                      </Button>
                    </div>
                  : 
                    <>
                      <CreateForm
                        size="medium"
                        allowEdit={false}
                        shape={loginShape}
                        defaultValues={{email, password, rememberMe}}
                        onChange={(vals, key) => {
                          const {email, password, rememberMe} = vals
                          setEmail(email)
                          setPassword(password)
                          setRememberMe(rememberMe)
                          setIsEdited(true)
                        }}
                      />
                      <Link
                        to="/reset-password"
                        // variant="body2"
                        // sx={{ alignSelf: 'baseline' }}
                      >
                        Forgot your password?
                      </Link>
                    </>
                  }
                </>
            }
          </div>
        </DialogContent>
        {!show2fa && !(showSuperAdminSelector && userAccountTemp) &&
          <>
            <DialogActions sx={{ pb: 3, px: 3 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button 
                id="login-modal-submit-button"
                variant="contained" 
                onClick={() =>{
                  onSubmit()
                }}
              >
                Login
              </Button>
            </DialogActions>
          </>
        }
          {/* :
          <>
            <DialogActions sx={{ pb: 3, px: 3 }}>
              <Button onClick={onClose}>Close</Button>
            </DialogActions>
          </>
        } */}
      </Dialog>
    </>
  );
}
